import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { fileService } from 'core/services';
import { IInfiniteQueryOptions } from 'core/types';
import { partService } from 'parts/services';
import {
  IPart,
  IPartInventoryItem,
  IPartInventoryPayload,
  IPartMedia,
  IPartMediaRequestPayload,
  IPartRequestPayload,
  IPartsSearchRequestPayload,
  ISalesHistoryItem,
  ISalesHistoryRequestPayload,
} from 'parts/types';

export const useGetPartsSearch = (
  requestPayload: IPartsSearchRequestPayload,
  options: { enabled?: boolean } = {}
) => useInfiniteQuery<IPart>(partService.getManySearch, 'partsSearch', requestPayload, options);

export const useGetPartInventory = (
  requestPayload: IPartInventoryPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<IPartInventoryItem>(
    partService.getPartInventory,
    'partInventory',
    requestPayload,
    options
  );

export const useGetSalesHistory = (
  requestPayload?: ISalesHistoryRequestPayload,
  options: IInfiniteQueryOptions<ISalesHistoryItem> = {}
) =>
  useInfiniteQuery<ISalesHistoryItem>(
    partService.getSalesHistory,
    'salesHistory',
    requestPayload,
    options
  );

export const useGetPart = (requestPayload: IPartRequestPayload, enabled = true) => {
  const { data, isError, isLoading } = useQuery(
    ['part', requestPayload],
    ({ signal }) => partService.getPart(requestPayload, signal),
    { enabled }
  );

  // part can be null while manufacturer still resolves — they're independent.
  return { part: data?.part, manufacturer: data?.manufacturer, isError, isLoading };
};

export const useGetPartMedia = (requestPayload: IPartMediaRequestPayload, enabled = true) => {
  const { data, isError, isLoading } = useQuery<IPartMedia>(
    ['partMedia', requestPayload],
    ({ signal }) => partService.getPartMedia(requestPayload, signal),
    { enabled }
  );

  return { record: data, isError, isLoading };
};

export const usePartActions = () => {
  return {
    ...useExportQuotePartDatasheets(),
  };
};

const useExportQuotePartDatasheets = () => {
  const { mutateAsync } = useMutation(partService.exportDatasheets);

  return { exportPartDatasheets: mutateAsync };
};

export const usePartMediaActions = () => {
  const { t } = useTranslation();

  const setDefaultImageErrors = useHandleErrors({
    success: { message: t('messageDefaultImageUpdateSuccess') },
    loading: { message: t('messageDefaultImageUpdateInProgress') },
    error: { title: t('errorTitleDefaultImageUpdate') },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['partMedia'],
  }).errors;

  const setDefaultDatasheetErrors = useHandleErrors({
    success: { message: t('messageDefaultDatasheetUpdateSuccess') },
    loading: { message: t('messageDefaultDatasheetUpdateInProgress') },
    error: { title: t('errorTitleDefaultDatasheetUpdate') },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['partMedia', 'part'],
  }).errors;

  const deleteFileErrors = useHandleErrors({
    success: { message: t('messagePartMediaFileRemoveSuccess') },
    loading: { message: t('messagePartMediaFileRemoveInProgress') },
    error: { title: t('errorTitlePartMediaFileRemove') },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['partMedia', 'part'],
  }).errors;

  const deleteVideoErrors = useHandleErrors({
    success: { message: t('messagePartVideoRemoveSuccess') },
    loading: { message: t('messagePartVideoRemoveInProgress') },
    error: { title: t('errorTitlePartVideoRemove') },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['partMedia'],
  }).errors;

  const { mutateAsync: setDefaultPartImage } = useMutation(
    partService.setDefaultPartImage,
    setDefaultImageErrors
  );

  const { mutateAsync: setDefaultPartDatasheet } = useMutation(
    partService.setDefaultPartDatasheet,
    setDefaultDatasheetErrors
  );

  const { mutateAsync: deletePartMediaFile } = useMutation(
    partService.deletePartMediaFile,
    deleteFileErrors
  );

  const { mutateAsync: deletePartVideo } = useMutation(
    partService.deletePartVideo,
    deleteVideoErrors
  );

  return {
    setDefaultPartImage,
    setDefaultPartDatasheet,
    deletePartMediaFile,
    deletePartVideo,
  };
};

export const useEditPartMediaFileDescription = ({
  position = NotificationPosition.DRAWER,
}: { position?: NotificationPosition } = {}) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messagePartMediaFileDescriptionUpdateSuccess') },
    loading: { message: t('messagePartMediaFileDescriptionUpdateInProgress') },
    error: { title: t('errorTitlePartMediaFileDescriptionUpdate') },
    position,
    invalidateQueries: ['partMedia'],
  });

  const { mutateAsync: editPartMediaFileDescription } = useMutation(
    fileService.editFileDescription,
    errors
  );

  return { editPartMediaFileDescription };
};

export const useSavePartVideo = ({
  position = NotificationPosition.DRAWER,
}: { position?: NotificationPosition } = {}) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messagePartVideoSaveSuccess') },
    loading: { message: t('messagePartVideoSaveInProgress') },
    error: { title: t('errorTitlePartVideoSave') },
    position,
    invalidateQueries: ['partMedia'],
  });

  const { mutateAsync: savePartVideo } = useMutation(partService.savePartVideo, errors);

  return { savePartVideo };
};

export const useSavePart = ({
  position = NotificationPosition.FORM,
}: { position?: NotificationPosition } = {}) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messagePartSaveSuccess') },
    loading: { message: t('messagePartSaveInProgress') },
    error: { title: t('errorTitlePartSave') },
    position,
    invalidateQueries: ['part', 'partSpecifications', 'partsSearch'],
  });

  const { mutateAsync: savePart } = useMutation(partService.savePart, errors);

  return { savePart };
};
